import React, { useMemo } from 'react';
import { Box, Button, Stack, Typography, IconButton, Tooltip, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Add, CopyAllOutlined, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';

import { ToneChip } from '../../common/StatusChip';
import type { SelectOption } from '../../common/SearchableSelect';
import { fmtClassName } from '../../../utils/pdf/format';
import { toFieldKey } from '../../../utils/fieldKey';

/**
 * Small display pieces shared by the cycle Settings sections, so every
 * section's read-only view speaks the same quiet language: label-over-value
 * facts, hairline lists, soft one-line notes and on/off pills.
 */

export interface SchoolClass { _id: string; name: string }

/** Machine key from a human label ("Birth certificate" → "birth_certificate"). */
export const toKey = (s: string, fallback: string): string => toFieldKey(s, fallback, 40);

/** Figures that line up in columns. */
export const numSx = { fontVariantNumeric: 'tabular-nums' } as const;

/** Class display names ("Class 9") + picker options, keyed by class id. */
export const useClassNames = (classes: SchoolClass[]) => useMemo(() => {
  const byId = new Map(classes.map(c => [c._id, fmtClassName(c.name)]));
  const options: SelectOption[] = classes.map(c => ({ value: c._id, label: fmtClassName(c.name) }));
  return {
    nameOf: (id?: string | null): string => (id && byId.get(id)) || '—',
    options,
  };
}, [classes]);

const asArray = (sx?: SxProps<Theme>) => (Array.isArray(sx) ? sx : sx ? [sx] : []);

/** Soft one-line note — empty states and "turned off" hints. */
export const QuietNote: React.FC<{ children: React.ReactNode; sx?: SxProps<Theme> }> = ({ children, sx }) => (
  <Box sx={[{ px: 2, py: 1.5, borderRadius: 2, bgcolor: (t) => alpha(t.palette.text.primary, 0.03) }, ...asArray(sx)]}>
    <Typography variant="body2" color="text.secondary">{children}</Typography>
  </Box>
);

/** Label-over-value grid: two columns on phones, `columns` from md up. */
export const FactGrid: React.FC<{ children: React.ReactNode; columns?: number }> = ({ children, columns = 2 }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${columns}, minmax(0, 1fr))` },
    columnGap: 4,
    rowGap: 2.5,
  }}>
    {children}
  </Box>
);

export const Fact: React.FC<{ label: React.ReactNode; children: React.ReactNode; muted?: boolean }> = ({ label, children, muted }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>{label}</Typography>
    <Typography component="div" variant="body2" fontWeight={muted ? 400 : 500} color={muted ? 'text.secondary' : 'text.primary'} sx={numSx}>
      {children}
    </Typography>
  </Box>
);

/** One row of a hairline list; place rows inside a `TableCard`. */
export const ListRow: React.FC<{ children: React.ReactNode; sx?: SxProps<Theme> }> = ({ children, sx }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={[{ px: 2, py: 1.5, '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } }, ...asArray(sx)]}
  >
    {children}
  </Stack>
);

export const OnOffPill: React.FC<{ on: boolean; onLabel?: string; offLabel?: string }> = ({ on, onLabel = 'On', offLabel = 'Off' }) => (
  <ToneChip label={on ? onLabel : offLabel} tone={on ? 'success' : 'neutral'} />
);

/** Reorder arrows for list editors. */
export const MoveButtons: React.FC<{ index: number; count: number; onMove: (dir: -1 | 1) => void }> = ({ index, count, onMove }) => (
  <>
    <Tooltip title="Move up">
      <span>
        <IconButton size="small" onClick={() => onMove(-1)} disabled={index === 0}>
          <KeyboardArrowUp fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
    <Tooltip title="Move down">
      <span>
        <IconButton size="small" onClick={() => onMove(1)} disabled={index === count - 1}>
          <KeyboardArrowDown fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  </>
);

// ─── Per-class tables: fill fast ───────────────────────────────────────────

/** The list with the item at `index` swapped one place up (-1) or down (1). */
export const moveAt = <T,>(items: T[], index: number, dir: -1 | 1): T[] => {
  const to = index + dir;
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
};

/** Every row takes `from`'s values for the fields `pick` returns. */
export const copyToAll = <T,>(rows: T[], from: number, pick: (row: T) => Partial<T>): T[] => {
  const values = pick(rows[from]);
  return rows.map(r => ({ ...r, ...values }));
};

/** Row action that copies the row's values to every other row. */
export const CopyToAllButton: React.FC<{ onClick: () => void; title?: string }> = ({ onClick, title = 'Use for every class' }) => (
  <Tooltip title={title}>
    <IconButton size="small" onClick={onClick} aria-label={title}>
      <CopyAllOutlined fontSize="small" />
    </IconButton>
  </Tooltip>
);

/**
 * Header actions for a table with one row per class: add the next class, or
 * every class not in the table yet in one go.
 */
export const AddClassButtons: React.FC<{
  /** Classes not in the table yet, in school order. */
  remaining: SchoolClass[];
  onAdd: (classes: SchoolClass[]) => void;
  label?: string;
  disabled?: boolean;
}> = ({ remaining, onAdd, label = 'Add class', disabled }) => (
  <Stack direction="row" spacing={0.5}>
    {remaining.length > 1 && (
      <Button
        size="small"
        onClick={() => onAdd(remaining)}
        disabled={disabled}
        sx={{ textTransform: 'none', color: 'text.secondary' }}
      >
        Add all {remaining.length}
      </Button>
    )}
    <Button
      size="small"
      startIcon={<Add fontSize="small" />}
      onClick={() => onAdd(remaining.slice(0, 1))}
      disabled={disabled || remaining.length === 0}
    >
      {label}
    </Button>
  </Stack>
);
